import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { applyOps, summarizeElement } from "@overlay/runtime-patcher";
import type { EditOp } from "@overlay/op-schema";
import { AssetPicker } from "./AssetPicker";

type Selection = ReturnType<typeof summarizeElement>;

type Draft = {
  textContent: string | null;
  styles: Record<string, string>;
  attrs: Record<string, string | null>;
};

const EMPTY_DRAFT: Draft = { textContent: null, styles: {}, attrs: {} };

export function InspectTab({
  selection,
  onKeep,
  apiBase,
  token,
  projectId,
}: {
  selection: Selection;
  onKeep: (ops: EditOp[]) => void;
  apiBase: string;
  token: string;
  projectId: string;
}) {
  const [pickerMode, setPickerMode] = useState<"src" | "background" | null>(null);
  const element = useMemo(() => {
    try {
      return document.querySelector(selection.selector) as HTMLElement | null;
    } catch {
      return null;
    }
  }, [selection.selector]);

  const initial = useMemo(() => readInitial(element), [element]);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const revertRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setDraft(EMPTY_DRAFT);
    revertRef.current?.();
    revertRef.current = null;
  }, [selection.selector]);

  useEffect(() => {
    return () => {
      revertRef.current?.();
      revertRef.current = null;
    };
  }, []);

  const reapply = (next: Draft) => {
    revertRef.current?.();
    revertRef.current = null;
    const ops = buildOps(selection.selector, next, selection.tag);
    setDraft(next);
    if (ops.length === 0) return;
    const result = applyOps(ops);
    revertRef.current = result.revert;
  };

  const patchStyle = (decl: Record<string, string | null>) => {
    const styles = { ...draft.styles };
    for (const [k, v] of Object.entries(decl)) {
      if (v === null || v === "") delete styles[k];
      else styles[k] = v;
    }
    reapply({ ...draft, styles });
  };

  const patchAttr = (name: string, value: string | null) => {
    const attrs = { ...draft.attrs };
    if (value === null) delete attrs[name];
    else attrs[name] = value;
    reapply({ ...draft, attrs });
  };

  const patchText = (v: string | null) => {
    reapply({ ...draft, textContent: v });
  };

  const discard = () => {
    revertRef.current?.();
    revertRef.current = null;
    setDraft(EMPTY_DRAFT);
  };

  const keep = () => {
    const ops = buildOps(selection.selector, draft, selection.tag);
    revertRef.current = null;
    setDraft(EMPTY_DRAFT);
    onKeep(ops);
  };

  const val = (key: string, fallback = "") =>
    draft.styles[key] ?? initial.styles[key] ?? fallback;
  const attrVal = (name: string, fallback = "") =>
    draft.attrs[name] ?? initial.attrs[name] ?? fallback;
  const isImg = selection.tag === "img";
  const hasText = !isImg && initial.hasDirectText;
  const dirty =
    draft.textContent !== null ||
    Object.keys(draft.styles).length > 0 ||
    Object.keys(draft.attrs).length > 0;

  return (
    <>
      {hasText && (
        <Section title="Content">
          <div class="field">
            <textarea
              class="prompt"
              style={{ minHeight: "60px" }}
              value={draft.textContent ?? initial.textContent}
              onInput={(e) => patchText((e.target as HTMLTextAreaElement).value)}
            />
          </div>
        </Section>
      )}

      <Section title="Typography">
        <ColorField
          label="Color"
          value={val("color", "#000000")}
          onChange={(v) => patchStyle({ color: v || null })}
        />
        <div class="grid-2">
          <LengthField
            label="Size"
            value={val("font-size")}
            onChange={(v) => patchStyle({ "font-size": v || null })}
          />
          <SelectField
            label="Weight"
            value={val("font-weight")}
            onChange={(v) => patchStyle({ "font-weight": v || null })}
            options={["100", "200", "300", "400", "500", "600", "700", "800", "900"]}
          />
        </div>
        <div class="field">
          <label>Align</label>
          <Segmented
            value={val("text-align", "left")}
            onChange={(v) => patchStyle({ "text-align": v || null })}
            options={[
              { value: "left", label: "L" },
              { value: "center", label: "C" },
              { value: "right", label: "R" },
              { value: "justify", label: "J" },
            ]}
          />
        </div>
        <LengthField
          label="Line height"
          value={val("line-height")}
          onChange={(v) => patchStyle({ "line-height": v || null })}
        />
      </Section>

      <Section title="Background">
        <ColorField
          label="Color"
          value={val("background-color", "#00000000")}
          onChange={(v) => patchStyle({ "background-color": v || null })}
          allowTransparent
        />
        <div class="field">
          <label>Image URL</label>
          <input
            class="input mono"
            value={extractBgUrl(val("background-image"))}
            placeholder="none"
            onInput={(e) => {
              const url = (e.target as HTMLInputElement).value.trim();
              patchStyle({ "background-image": url ? `url('${url}')` : null });
            }}
          />
          <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
            <button class="btn" onClick={() => setPickerMode("background")}>
              Browse library…
            </button>
            <button
              class="btn"
              onClick={() => patchStyle({ "background-image": "none" })}
            >
              Clear
            </button>
          </div>
        </div>
        <div class="grid-2">
          <SelectField
            label="Size"
            value={val("background-size")}
            onChange={(v) => patchStyle({ "background-size": v || null })}
            options={["", "cover", "contain", "auto"]}
          />
          <SelectField
            label="Position"
            value={val("background-position")}
            onChange={(v) => patchStyle({ "background-position": v || null })}
            options={["", "center", "top", "bottom", "left", "right", "top left", "top right", "bottom left", "bottom right"]}
          />
        </div>
      </Section>

      <Section title="Spacing">
        <div class="field">
          <label>Padding (t / r / b / l)</label>
          <div class="grid-4">
            {(["padding-top", "padding-right", "padding-bottom", "padding-left"] as const).map((p) => (
              <input
                key={p}
                class="input mono"
                value={val(p)}
                placeholder="0"
                onInput={(e) =>
                  patchStyle({ [p]: (e.target as HTMLInputElement).value || null })
                }
              />
            ))}
          </div>
        </div>
        <div class="field">
          <label>Margin (t / r / b / l)</label>
          <div class="grid-4">
            {(["margin-top", "margin-right", "margin-bottom", "margin-left"] as const).map((p) => (
              <input
                key={p}
                class="input mono"
                value={val(p)}
                placeholder="0"
                onInput={(e) =>
                  patchStyle({ [p]: (e.target as HTMLInputElement).value || null })
                }
              />
            ))}
          </div>
        </div>
      </Section>

      <Section title="Size">
        <div class="grid-2">
          <LengthField
            label="Width"
            value={val("width")}
            onChange={(v) => patchStyle({ width: v || null })}
          />
          <LengthField
            label="Height"
            value={val("height")}
            onChange={(v) => patchStyle({ height: v || null })}
          />
        </div>
      </Section>

      <Section title="Border">
        <SliderField
          label="Radius"
          value={val("border-radius", "0px")}
          min={0}
          max={64}
          onChange={(v) => patchStyle({ "border-radius": v || null })}
        />
        <div class="grid-2">
          <LengthField
            label="Width"
            value={val("border-width")}
            onChange={(v) =>
              patchStyle({ "border-width": v || null, "border-style": v ? "solid" : null })
            }
          />
          <ColorField
            label="Color"
            value={val("border-color", "#000000")}
            onChange={(v) => patchStyle({ "border-color": v || null })}
          />
        </div>
      </Section>

      <Section title="Layout">
        <SelectField
          label="Display"
          value={val("display")}
          onChange={(v) => patchStyle({ display: v || null })}
          options={["", "block", "inline", "inline-block", "flex", "grid", "none"]}
        />
      </Section>

      {isImg && (
        <Section title="Image">
          <div class="field">
            <label>Source</label>
            <input
              class="input mono"
              value={attrVal("src")}
              onInput={(e) => patchAttr("src", (e.target as HTMLInputElement).value || null)}
            />
            <button
              class="btn"
              style={{ marginTop: "6px" }}
              onClick={() => setPickerMode("src")}
            >
              Browse library…
            </button>
          </div>
          <div class="field">
            <label>Alt</label>
            <input
              class="input"
              value={attrVal("alt")}
              onInput={(e) => patchAttr("alt", (e.target as HTMLInputElement).value)}
            />
          </div>
          <SelectField
            label="Fit"
            value={val("object-fit")}
            onChange={(v) => patchStyle({ "object-fit": v || null })}
            options={["", "cover", "contain", "fill", "none", "scale-down"]}
          />
        </Section>
      )}

      {dirty && (
        <div class="commit-bar">
          <span class="muted" style={{ fontSize: "11px" }}>Preview applied</span>
          <div class="spacer" />
          <button class="btn" onClick={discard}>Discard</button>
          <button class="btn primary" onClick={keep}>Keep</button>
        </div>
      )}

      {pickerMode !== null && (
        <AssetPicker
          apiBase={apiBase}
          token={token}
          canUpload={projectId !== "demo"}
          onPick={(url) => {
            if (pickerMode === "src") {
              patchAttr("src", url);
            } else {
              const next: Record<string, string> = {
                "background-image": `url('${url}')`,
              };
              const curSize = draft.styles["background-size"] ?? initial.styles["background-size"];
              const curPos = draft.styles["background-position"] ?? initial.styles["background-position"];
              if (!curSize || curSize === "auto") next["background-size"] = "cover";
              if (!curPos) next["background-position"] = "center";
              patchStyle(next);
            }
            setPickerMode(null);
          }}
          onClose={() => setPickerMode(null)}
        />
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: preact.ComponentChildren }) {
  return (
    <div class="inspect-section">
      <div class="inspect-section-header">
        <span>{title}</span>
      </div>
      <div class="inspect-section-body">{children}</div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
  allowTransparent,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  allowTransparent?: boolean;
}) {
  const hex = toHex(value) || "#000000";
  return (
    <div class="field-inline">
      <label>{label}</label>
      <div class="color-swatch">
        <input
          type="color"
          value={hex}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        />
        <input
          type="text"
          value={value}
          placeholder={allowTransparent ? "transparent" : hex}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        />
      </div>
    </div>
  );
}

function LengthField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div class="field">
      <label>{label}</label>
      <input
        class="input mono"
        value={value}
        placeholder="auto"
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div class="field">
      <label>{label}</label>
      <select
        class="select"
        value={value}
        onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o || "—"}
          </option>
        ))}
      </select>
    </div>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div class="seg">
      {options.map((o) => (
        <button
          key={o.value}
          class={value === o.value ? "active" : ""}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SliderField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min: number;
  max: number;
  onChange: (v: string) => void;
}) {
  const num = parseInt(value, 10);
  const v = Number.isFinite(num) ? num : min;
  return (
    <div class="field">
      <label>{label}</label>
      <div class="slider-row">
        <input
          type="range"
          min={min}
          max={max}
          value={v}
          onInput={(e) => onChange(`${(e.target as HTMLInputElement).value}px`)}
        />
        <span class="value">{v}px</span>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function readInitial(el: HTMLElement | null) {
  const styles: Record<string, string> = {};
  const attrs: Record<string, string> = {};
  let textContent = "";
  let hasDirectText = false;
  if (el) {
    const cs = getComputedStyle(el);
    const keys = [
      "color", "background-color", "background-image", "background-size",
      "background-position", "font-size", "font-weight", "text-align",
      "line-height", "padding-top", "padding-right", "padding-bottom", "padding-left",
      "margin-top", "margin-right", "margin-bottom", "margin-left",
      "width", "height", "border-radius", "border-width", "border-color",
      "display", "object-fit",
    ];
    for (const k of keys) {
      const v = cs.getPropertyValue(k).trim();
      if (v && v !== "none") styles[k] = v;
    }
    for (const a of Array.from(el.attributes)) attrs[a.name] = a.value;

    // "Direct" text: the element has a text-node child directly
    hasDirectText = Array.from(el.childNodes).some(
      (n) => n.nodeType === 3 && (n.textContent ?? "").trim().length > 0,
    );
    if (hasDirectText) textContent = el.textContent ?? "";
  }
  return { styles, attrs, textContent, hasDirectText };
}

function buildOps(selector: string, draft: Draft, tag: string): EditOp[] {
  const ops: EditOp[] = [];
  const target = { selector };

  if (Object.keys(draft.styles).length > 0) {
    ops.push({
      id: `inspect-style-${Date.now()}`,
      type: "style.set",
      target,
      declarations: draft.styles,
    });
  }

  for (const [name, value] of Object.entries(draft.attrs)) {
    if (value === null) {
      ops.push({ id: `inspect-attr-rm-${name}-${Date.now()}`, type: "attr.remove", target, name });
    } else if (name === "src" && tag === "img") {
      ops.push({
        id: `inspect-src-${Date.now()}`,
        type: "asset.replace",
        target,
        attr: "src",
        after: value,
      });
    } else {
      ops.push({
        id: `inspect-attr-${name}-${Date.now()}`,
        type: "attr.set",
        target,
        name,
        value,
      });
    }
  }

  if (draft.textContent !== null) {
    ops.push({
      id: `inspect-text-${Date.now()}`,
      type: "text.set",
      target,
      after: draft.textContent,
    });
  }

  return ops;
}

function extractBgUrl(input: string): string {
  if (!input || input === "none") return "";
  const m = /url\(\s*(['"]?)([^'")]+)\1\s*\)/i.exec(input);
  return m && m[2] ? m[2] : "";
}

function toHex(input: string): string | null {
  if (!input) return null;
  if (/^#[0-9a-f]{6}$/i.test(input)) return input.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(input)) {
    const [, a, b, c] = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(input)!;
    return `#${a}${a}${b}${b}${c}${c}`.toLowerCase();
  }
  const m = /^rgba?\(([^)]+)\)$/i.exec(input);
  if (m && m[1]) {
    const parts = m[1].split(",").map((s) => parseInt(s.trim(), 10));
    const r = parts[0], g = parts[1], b = parts[2];
    if (
      typeof r === "number" && typeof g === "number" && typeof b === "number" &&
      Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)
    ) {
      const toH = (n: number) => n.toString(16).padStart(2, "0");
      return `#${toH(r)}${toH(g)}${toH(b)}`.toLowerCase();
    }
  }
  return null;
}
